import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Heading from "@tiptap/extension-heading";
import Blockquote from "@tiptap/extension-blockquote";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import CodeBlock from "@tiptap/extension-code-block";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Strike from "@tiptap/extension-strike";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Dropcursor from "@tiptap/extension-dropcursor";
import Gapcursor from "@tiptap/extension-gapcursor";

import Toolbar from "../Toolbar";
import {
  ThemeProvider,
  Paper,
  TextField,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
} from "@mui/material";
import darkTheme from "../dashboardTheme";

const PostForm = ({ post, onSave, onEdit, onCancel, showActive = true }) => {
  const [title, setTitle] = useState(post?.title || "");
  const [content, setContent] = useState(
    post?.content || "<p>Start writing...</p>"
  );
  const [isActive, setIsActive] = useState(post?.status === "active");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Blockquote,
      BulletList,
      OrderedList,
      ListItem,
      CodeBlock.configure({ languageClassPrefix: "language-" }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: true, allowBase64: true }),
      TextAlign.configure({ types: ["heading", "paragraph", "image"] }),
      Strike,
      Color,
      TextStyle,
      Subscript,
      Superscript,
      HorizontalRule,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      Dropcursor,
      Gapcursor,
    ],
    content,
    onUpdate: ({ editor }) => setContent(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose dark:prose-invert max-w-none focus:outline-none",
      },
    },
  });

  const handleSubmit = () => {
    const updatedPost = {
      ...post,
      title,
      content,
      status: isActive ? "active" : "inactive",
    };

    if (post?._id) {
      onEdit(updatedPost);
    } else {
      onSave(updatedPost);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Paper
        elevation={4}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          color: "text.primary",
          mb: 3,
        }}
      >
        {/* Title Input */}
        <TextField
          label="Post Title"
          variant="outlined"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Active Status */}
        {showActive && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
                color={isActive ? "success" : "error"}
              />
            }
            label={
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: isActive ? "#4caf50" : "#f44336",
                }}
                p={0}
                m={0}
              >
                {isActive ? "Active" : "Inactive"}
              </Typography>
            }
            sx={{ mb: 2 }}
          />
        )}

        {/* Toolbar */}
        <Toolbar editor={editor} />

        {/* Editor Content */}
        <Box
          sx={{
            mt: 2,
            minHeight: "300px",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            bgcolor: "background.default",
            overflowY: "auto",
            "& .ProseMirror": {
              outline: "none",
              color: "text.primary",
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ fontWeight: "bold" }}
          >
            Save Post
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onCancel}
            sx={{ fontWeight: "bold" }}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>
    </ThemeProvider>
  );
};

export default PostForm;
